import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Clock, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { getUserTimezone, getTimezoneInfo } from "@/lib/timezone/timezone-service";

interface DateTimeDisplayProps {
  selectedDate: Date;
  selectedTime?: string;
  duration?: number;
  timezone?: string;
  organizerTimezone?: string; // Add the missing prop
  className?: string;
  onBackToDateStep?: () => void; // NEW PROP
  onTimezoneChange?: (timezone: string) => void;
  isOptimalSlot?: boolean; // NEW: indicates if the selected time is optimal
}

function getTimezoneAbbreviation(date: Date, timeZone: string): string {
  // Example: timeZone = "America/Chicago"
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find(part => part.type === 'timeZoneName');
  return tzPart ? tzPart.value : '';
}


export const DateTimeDisplay: React.FC<DateTimeDisplayProps> = ({
  selectedDate,
  selectedTime,
  duration,
  timezone,
  organizerTimezone, // Add to destructuring
  className,
  onBackToDateStep, // NEW
  onTimezoneChange,
  isOptimalSlot = false // NEW: default to false
}) => {
  const [showSparkleEffect, setShowSparkleEffect] = useState(false);
  const [prevIsOptimalSlot, setPrevIsOptimalSlot] = useState(false);

  // Trigger sparkle effect when optimal slot is first selected
  useEffect(() => {
    if (isOptimalSlot && !prevIsOptimalSlot && selectedTime) {
      setShowSparkleEffect(true);
      
      // Optional: Add sound effect here in the future
      // playSparkleSound();
      
      // Hide the effect after animation completes
      const timer = setTimeout(() => {
        setShowSparkleEffect(false);
      }, 1500); // 1.5 seconds for a subtle effect
      
      return () => clearTimeout(timer);
    }
    setPrevIsOptimalSlot(isOptimalSlot);
  }, [isOptimalSlot, prevIsOptimalSlot, selectedTime]);
  const userTimezone = getUserTimezone();
  let timezoneAbbr = '';
  if (selectedDate instanceof Date && !isNaN(selectedDate.getTime()) && timezone) {
    timezoneAbbr = getTimezoneAbbreviation(selectedDate, timezone);
  } else if (timezone) {
    // fallback: get abbreviation for current time in selected timezone
    try {
      timezoneAbbr = getTimezoneInfo(timezone).abbreviation;
    } catch {}
  }


  if (!(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) {
    // 'Select a date' state
    return (
      <div className={cn("bg-black/40 backdrop-blur-2xl rounded-xl sm:rounded-2xl p-1.5 sm:p-2 pr-1 sm:pr-1.5 md:p-3 md:pr-6 w-1/2 md:w-full md:m-left", className)}>
        <div className="flex flex-row h-10 sm:h-12 md:h-16 items-stretch">
          <div className="bg-white rounded-lg sm:rounded-xl p-1 sm:p-1.5 md:p-2 shadow-lg min-w-[40px] sm:min-w-[50px] md:min-w-[70px] text-center h-full flex flex-col justify-center">
            <div className="flex justify-center mt-0 lg:-mt-1 content-center">
              <Calendar className="flex w-4 h-4 sm:w-5 sm:h-5 md:w-10 md:h-10 md:my-2.5 items-center gap-1"/>
            </div>
          </div>
          <div className="flex-1 flex flex-col justify-center pl-2 sm:pl-3 md:pl-4 text-white min-w-0">
            <div className="flex items-center justify-between w-full">
              <h3 className="text-md sm:text-sm md:text-lg lg:text-xl font-semibold mb-0 lg:mb-0 mt-0.5 sm:mt-1 truncate">
                Select a date
              </h3>
            </div>
            <div className="text-md sm:text-sm md:text-lg text-white/50">
              <div className="flex items-center mt-0">
                <svg className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="truncate">{duration} min</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Selected date or timeslot state
  const monthAbbr = format(selectedDate, 'MMM').toUpperCase();
  const dayNumber = format(selectedDate, 'd');
  const dayName = format(selectedDate, 'EEEE');
  let startTime, endTime;
  if (selectedTime && duration && timezone) {
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const startDateTime = new Date(selectedDate);
    if (!isNaN(startDateTime.getTime())) {
      startDateTime.setHours(hours, minutes, 0, 0);
      const endDateTime = new Date(startDateTime);
      endDateTime.setMinutes(endDateTime.getMinutes() + duration);
      startTime = format(startDateTime, 'h:mm a');
      endTime = format(endDateTime, 'h:mm a');
    }
  }

  return (
    <div className="relative">
      {/* Subtle Sparkle Effect Overlay */}
      {showSparkleEffect && (
        <div className="absolute inset-0 z-20 pointer-events-none">
          {/* Just a few subtle sparkles */}
          <div className="absolute top-1/3 left-1/3 w-1 h-1 bg-purple-300 rounded-full animate-ping opacity-60" style={{ animationDelay: '0s', animationDuration: '2s' }} />
          <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-pink-300 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.5s', animationDuration: '2s' }} />
          
          {/* Very subtle gradient glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 via-pink-400/5 to-orange-400/10 rounded-2xl animate-pulse" style={{ animationDuration: '3s' }} />
        </div>
      )}
      
      <div
        className={cn(
          "backdrop-blur-sm  rounded-xl sm:rounded-2xl p-1.5 sm:p-2 pr-1 sm:pr-1.5 md:p-3 md:pr-6 w-1/2 md:w-full md:m-left transition-all duration-500 relative z-10",
          isOptimalSlot && selectedTime
            ? "bg-gradient-to-r from-purple-500/40 via-pink-500/40 to-orange-500/40 border border-purple-300/0 shadow-lg shadow-purple-500/5"
            : "bg-black/40",
          onBackToDateStep && "cursor-pointer hover:bg-black/60",
          className
        )}
        onClick={onBackToDateStep}
        tabIndex={onBackToDateStep ? 0 : undefined}
        role={onBackToDateStep ? "button" : undefined}
        aria-label={onBackToDateStep ? "Change date" : undefined}
      >
      <div className="flex flex-row h-10 sm:h-12 md:h-16 items-stretch">
        <div className={cn(
          "rounded-lg sm:rounded-xl p-2 sm:p-1.5 md:p-2 shadow-lg min-w-[40px] sm:min-w-[50px] md:min-w-[70px] text-center h-full flex flex-col justify-center transition-all duration-300",
          isOptimalSlot && selectedTime
            ? "bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100 border border-purple-200 shadow-lg shadow-purple-500/20"
            : "bg-white"
        )}>
          <div className={cn(
            "text-sm font-semibold leading-none",
            isOptimalSlot && selectedTime ? "text-purple-700" : "text-gray-500"
          )}>
            {monthAbbr}
          </div>
          <div className={cn(
            "text-md sm:text-lg md:text-4xl font-bold leading-none mt-0 lg:-mt-1",
            isOptimalSlot && selectedTime ? "text-purple-900" : "text-gray-900"
          )}>
            {dayNumber}
          </div>
        </div>
        <div className="flex-1 flex flex-col justify-center pl-2 sm:pl-3 md:pl-4 text-white min-w-0">
          <div className="flex items-center justify-between w-fit">
            <h3 className="text-md sm:text-md md:text-lg font-medium mb-0 lg:mb-0 mt-0.5 sm:mt-1 truncate">
              {dayName}
            </h3>
          </div>
          <div className="font-medium text-md sm:text-md md:text-lg text-white/70 flex items-center gap-1 sm:gap-2 min-w-0">
            {(startTime && endTime && timezoneAbbr) ? (
              <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-wrap">
                {/* {isOptimalSlot && (
                  <div className="flex items-center gap-0.5 sm:gap-1 px-1 sm:px-1.5 md:px-2 py-0.5 sm:py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full border border-purple-300/30 animate-pulse flex-shrink-0">
                    <Dna className="h-2 w-2 sm:h-2.5 sm:w-2.5 md:h-3 md:w-3 text-purple-300" />
                    <span className="text-xs font-medium text-purple-200 hidden sm:inline">Optimal</span>
                    <span className="text-xs font-medium text-purple-200 sm:hidden">Opt</span>
                  </div>
                )} */}
                <span className={cn(
                  "truncate text-md sm:text-md md:text-base",
                  isOptimalSlot ? "text-purple-200 font-semibold" : "text-white/70"
                )}>
                  {startTime} - {endTime}
                </span>
              </div>
            ) : (
              <span className="text-md sm:text-md md:text-lg text-white/30">Select a timeslot</span>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
